'use Client';
import Link from 'next/link';
import React, { useState, useEffect, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Image from 'next/legacy/image';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import { MARKET_NAMES, SEE_MORE_TRANSLATION, STORAGE_KEYS } from '@/constants/const';
import AuthContext from '@/context/AuthContext';
import { findRegion, tryCatchHandlerSync } from '@/utils/commonUtils';
import { findAndConvertTextToVNOrgs } from '@/utils/commonUtilsTS';
import useMarket from '@/hooks/useMarket';

/** @type {React.NamedExoticComponent<{ maintenanceBanners: any[] }>} */
const MaintenanceTile = React.memo((props) => {
    const isClient = typeof window !== 'undefined';
    const { publicRuntimeConfig } = getConfig();

    /** @type {ReturnType<typeof useState<any[]>>} */
    const [maintenanceBanners, setMaintenanceBanners] = useState([]);
    const isVNMarket = publicRuntimeConfig.COUNTRY_CODE === MARKET_NAMES.VN;
    const isKRMarket = publicRuntimeConfig.COUNTRY_CODE === MARKET_NAMES.KR;
    const VN_DOMAINS = JSON.parse(publicRuntimeConfig.VN_DOMAINS);
    const { auth } = useContext(AuthContext);
    const lang = auth?.language;
    const { isHKMarket } = useMarket();

    const convertContentToVNOrgs = (data, saleOrgDictionary) => {
        const regionCode = findRegion(isVNMarket, VN_DOMAINS);
        if (data) {
            let str = data;
            saleOrgDictionary?.map((item) => {
                const text = item.key.value;
                if (data?.includes(text)) {
                    const convertText = findAndConvertTextToVNOrgs(item, regionCode);
                    if (convertText !== '') {
                        str = str.replaceAll(text, convertText);
                    }
                }
            });
            return str;
        } 
            return '';
        
    };

    useEffect(() => {
        if (!isClient) {
            return;
        }

        let maintenanceBanners = props.maintenanceBanners;

        const cookieName = 'MaintenanceBannersWereHidedUntilTomorrow';
        const cookieValue = Cookies.get(cookieName) ? JSON.parse(Cookies.get(cookieName)) : [];
        const closeMaintainenceBanner =
            tryCatchHandlerSync(() => JSON.parse(localStorage.getItem(STORAGE_KEYS.CLOSE_MAINTENANCE_BANNER) || ''))
                .data || [];
        if (cookieValue.length > 0) {
            maintenanceBanners = maintenanceBanners.filter(
                (maintenanceBanner) => !cookieValue.includes(maintenanceBanner.id)
            );
        }
        if (closeMaintainenceBanner.length > 0) {
            maintenanceBanners = maintenanceBanners.filter(
                (maintenanceBanner) => !closeMaintainenceBanner.includes(maintenanceBanner.id)
            );
        }
        setMaintenanceBanners(maintenanceBanners);
    }, [props.maintenanceBanners]);

    const handleClose = (maintenanceBannerId) => {
        setMaintenanceBanners((prevBanners) => prevBanners?.filter((banner) => banner.id !== maintenanceBannerId));
        const maintenanceBannerCloseArr =
            tryCatchHandlerSync(() => JSON.parse(localStorage.getItem(STORAGE_KEYS.CLOSE_MAINTENANCE_BANNER) || ''))
                .data || [];
        maintenanceBannerCloseArr.push(maintenanceBannerId);
        localStorage.setItem(STORAGE_KEYS.CLOSE_MAINTENANCE_BANNER, JSON.stringify(maintenanceBannerCloseArr));
    };

    const handleDoNotShowUntilTomorrow = (maintenanceBannerId) => {
        const cookieName = 'MaintenanceBannersWereHidedUntilTomorrow';
        const cookieValue = Cookies.get(cookieName) ? JSON.parse(Cookies.get(cookieName)) : [];

        if (!cookieValue.includes(maintenanceBannerId)) {
            cookieValue.push(maintenanceBannerId);
        }

        const expirationDate = new Date();
        expirationDate.setHours(23, 59, 59, 999);

        Cookies.set(cookieName, JSON.stringify(cookieValue), { expires: expirationDate });

        setMaintenanceBanners((prevBanners) => prevBanners?.filter((banner) => banner.id !== maintenanceBannerId));
    };

    return (
        maintenanceBanners.length > 0 ? (
                <div
                    id='maintenanceBanner'
                    className=' relative bg-[#FAC4C4] w-full px-4 min-[391px]:px-6 laptop:px-[108px] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-[#F7A8A8] z-[3]'>
                    {maintenanceBanners.map((maintenanceData) => {
                        const htmlString = isVNMarket
                            ? convertContentToVNOrgs(
                                  maintenanceData?.content?.value,
                                  maintenanceData?.saleOrgDictionary?.value
                              )
                            : maintenanceData
                            ? maintenanceData?.content?.value
                            : '';
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(htmlString, 'text/html');
                        const element = doc.querySelectorAll('*:last-child');
                        // On the HK market pre-login page, the default language is EN.
                        const text = lang === 'EN' || (lang === '' && isHKMarket) ? 'more' : SEE_MORE_TRANSLATION[publicRuntimeConfig.COUNTRY_CODE];
                        element?.forEach((ele, index) => {
                            const lastTag = element[element.length - 1].tagName;
                            if (maintenanceData?.hyperlink?.jsonValue?.value?.url) {
                                if ((lastTag === 'SPAN' || lastTag === 'P') && index === element.length - 1) {
                                    if (
                                        ele?.parentNode?.['tagName'] === 'SPAN' ||
                                        ele?.parentNode?.['tagName'] === 'P'
                                    ) {
                                        element[index - 1].innerHTML =
                                            element[index - 1].innerHTML +
                                            ` <a style='font-size: 12px; font-weight: bold; text-decoration: underline; cursor: pointer' 
                                        href=${maintenanceData?.hyperlink?.jsonValue?.value?.url}>${text}
                                        </a>`;
                                    } else {
                                        ele.innerHTML =
                                            ele.innerHTML +
                                            ` <a style='font-size: 12px; font-weight: bold; text-decoration: underline; cursor: pointer' 
                                        href=${maintenanceData?.hyperlink?.jsonValue?.value?.url}>${text}
                                        </a>`;
                                    }
                                } else if (lastTag !== 'SPAN' && lastTag !== 'P' && index === 0) {
                                    ele.innerHTML =
                                        ele.innerHTML +
                                        ` <a style='font-size: 12px; font-weight: bold; text-decoration: underline; cursor: pointer' 
                                    href=${maintenanceData?.hyperlink?.jsonValue?.value?.url}>${text}
                                    </a>`;
                                }
                            }
                        });
                        const editorHTML = doc.documentElement.outerHTML;
                        return (
                            <div
                                key={maintenanceData.id}
                                className={`py-2 flex justify-between gap-6 ${!auth?.ok && 'max-w-[1224px] m-auto'}`}>
                                <div className='w-full flex gap-2'>
                                    <span className='bg-[#234455] h-fit px-2 py-[2px] rounded-[12px] text-[#E9EDEE] text-center text-[12px] leading-normal min-w-max'>
                                        <FormattedMessage id='TopNotice.alert' />
                                    </span>
                                    <div className='flex-1'>
                                        <span
                                            className={`${
                                                isKRMarket ? 'text-[16px] leading-[24px]' : 'text-xs leading-[18px]'
                                            } text-darkGreen font-normal hidden md:inline-block`}
                                            dangerouslySetInnerHTML={{
                                                __html: `${editorHTML}`
                                            }}
                                            data-testid='maintenance-content' />
                                        <span
                                            className={`${
                                                isKRMarket ? 'text-[16px] leading-[24px]' : 'text-xs leading-[18px]'
                                            } text-darkGreen font-normal inline-block md:hidden`}
                                            dangerouslySetInnerHTML={{
                                                __html: `${
                                                    isVNMarket
                                                        ? convertContentToVNOrgs(
                                                              maintenanceData?.content?.value,
                                                              maintenanceData?.saleOrgDictionary?.value
                                                          )
                                                        : maintenanceData
                                                        ? maintenanceData?.content?.value
                                                        : ''
                                                }`
                                            }} />
                                        <div className='flex inline-block md:hidden'>
                                            {maintenanceData?.hyperlink?.jsonValue?.value?.url && (
                                                <Link
                                                    href={`${maintenanceData?.hyperlink?.jsonValue?.value?.url}`}
                                                    legacyBehavior>
                                                    <a className='text-darkGreen font-bold underline text-xs cursor-pointer'>
                                                        <FormattedMessage id='TopNotice.seeMore' />
                                                    </a>
                                                </Link>
                                            )}
                                            <div className='md:min-w-max ml-auto'>
                                                {maintenanceData?.enableCrossButton?.value && (
                                                    <div className='flex gap-2 justify-end'>
                                                        <button
                                                            className='text-darkGreen text-xs font-bold underline'
                                                            onClick={() => {
                                                                handleDoNotShowUntilTomorrow(maintenanceData.id);
                                                            }}>
                                                            <FormattedMessage id='TopNotice.doNotShowUntilTomorrow' />
                                                        </button>
                                                        <div className='min-w-[11px] min-h-[11px]'>
                                                            <Image
                                                                src={
                                                                    'https://ezrxplus-uat-cdn.zuelligpharma.com/public/images/CloseMB.svg'
                                                                }
                                                                height={11}
                                                                width={11}
                                                                className='cursor-pointer'
                                                                alt='close'
                                                                onClick={() => {
                                                                    handleClose(maintenanceData.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='md:min-w-max hidden md:inline-block'>
                                    {maintenanceData?.enableCrossButton?.value && (
                                        <div className='flex gap-2 justify-end'>
                                            <button
                                                className='text-darkGreen text-xs font-bold underline'
                                                onClick={() => {
                                                    handleDoNotShowUntilTomorrow(maintenanceData.id);
                                                }}
                                                data-testid='do-not-show-until-tomorrow'>
                                                <FormattedMessage id='TopNotice.doNotShowUntilTomorrow' />
                                            </button>
                                            <div className='min-w-[11px] min-h-[11px]'>
                                                <Image
                                                    src={
                                                        'https://ezrxplus-uat-cdn.zuelligpharma.com/public/images/CloseMB.svg'
                                                    }
                                                    height={11}
                                                    width={11}
                                                    className='cursor-pointer'
                                                    alt='close'
                                                    data-testid='close-maintenance-banner'
                                                    onClick={() => {
                                                        handleClose(maintenanceData.id);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null
    );
});

MaintenanceTile.displayName = 'MaintenanceTile';

export default MaintenanceTile;
